import { useTranslation } from 'react-i18next';

import { ChoiceCards } from '@/shared/components/selection/ChoiceCards/ChoiceCards';
import { Input } from '@/shared/components/ui/Input/Input';
import { Label } from '@/shared/components/ui/Label/Label';

import { type PromoDurationFieldProps } from './PromoDurationField.types';
import { usePromoDurationFieldLogic } from './usePromoDurationFieldLogic';

const DAYS_INPUT_ID = 'promo-duration-days';

/**
 * How long a comp lasts — including the two answers a number box cannot give.
 *
 * *The plan's own duration* is an absent key and *never expires* is a `null`, and
 * neither is expressible by leaving an input empty (U0). The day count appears
 * only under the middle card: a number sitting beside "never expires" would be a
 * value on screen that is not going to be sent (P10.6e2).
 */
export function PromoDurationField(props: PromoDurationFieldProps) {
  const { t } = useTranslation('payments');
  const { label, describedById, onBlur, isDisabled = false } = props;
  const view = usePromoDurationFieldLogic(props);

  return (
    <div
      role="group"
      aria-label={label}
      aria-describedby={describedById}
      className="flex flex-col gap-md"
      onBlur={onBlur}
    >
      <ChoiceCards
        label={t('entitlements.grant.duration.mode')}
        value={view.mode}
        isDisabled={isDisabled}
        items={[
          {
            value: 'plan',
            label: t('entitlements.grant.duration.plan'),
            description: t('entitlements.grant.duration.planHint'),
          },
          {
            value: 'days',
            label: t('entitlements.grant.duration.days'),
            description: t('entitlements.grant.duration.daysHint'),
          },
          {
            value: 'forever',
            label: t('entitlements.grant.duration.forever'),
            description: t('entitlements.grant.duration.foreverHint'),
          },
        ]}
        onValueChange={view.chooseMode}
      />

      {view.isCounted ? (
        <div className="flex w-40 flex-col gap-xs">
          <Label htmlFor={DAYS_INPUT_ID}>{t('entitlements.grant.duration.dayCount')}</Label>
          <Input
            id={DAYS_INPUT_ID}
            type="number"
            inputMode="numeric"
            value={String(view.days)}
            min={view.min}
            max={view.max}
            disabled={isDisabled}
            onChange={(event) => {
              view.setDays(event.target.value);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
